import * as React from 'react';
import { DefaultButton, mergeStyleSets, IDropdownOption, getTheme } from '@fluentui/react';

type Key = string | number;

const GenderIcon: React.FC<{ tipo: 'F' | 'M'; size?: number; color?: string }> = ({
  tipo,
  size = 16,
  color = '#6b7280',
}) => {
  if (tipo === 'M') {
    // ♂: círculo + flecha
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false">
        <circle cx="10" cy="14" r="4" stroke={color} strokeWidth="2" fill="none" />
        <line x1="13" y1="11" x2="19" y2="5" stroke={color} strokeWidth="2" strokeLinecap="round" />
        <polyline
          points="15,5 19,5 19,9"
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  // ♀: círculo + cruz
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false">
      <circle cx="12" cy="9" r="4" stroke={color} strokeWidth="2" fill="none" />
      <line x1="12" y1="13" x2="12" y2="20" stroke={color} strokeWidth="2" strokeLinecap="round" />
      <line x1="9" y1="17" x2="15" y2="17" stroke={color} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
};

const classes = mergeStyleSets({
  group: {
    display: 'inline-flex',
    borderRadius: 8,
    overflow: 'hidden',
    border: '1px solid #e1e1e1',
  },
  btn: { borderRadius: 0, minWidth: 140 },
  divider: { width: 1, background: '#e1e1e1' },
});

// --- NUEVO: helper para mapear visual según género ---
function getGenderVisual(k: string, label?: string) {
  const s = (label ?? k).toLowerCase();
  const isFemale = s.startsWith('f') || s.includes('fem');
  const isMale = s.startsWith('m') || s.includes('masc');

  // colores comunes
  const DIS_BG = '#f3f4f6'; // fondo disabled
  const DIS_TXT = '#9ca3af'; // texto/icono disabled
  const TXT_BASE = '#0c1c4c'; // texto normal

  if (isFemale) {
    return {
      // intensidades para seleccionado/hover
      bgSelected: '#fb64afff', // rosa 500 aprox.
      bgHover: '#f74897ff', // rosa 600 aprox.
      border: '#f472b6', // borde/focus
      iconBase: '#f472b6', // icono cuando NO está seleccionado
      textBase: TXT_BASE,
      // helpers de color finales
      labelColor: (selected: boolean, disabled: boolean) =>
        disabled ? DIS_TXT : selected ? '#fff' : TXT_BASE,
      iconColor: (selected: boolean, disabled: boolean) =>
        disabled ? DIS_TXT : selected ? '#fff' : '#f472b6',
      disabledBg: DIS_BG,
      disabledTxt: DIS_TXT,
    };
  }

  if (isMale) {
    return {
      bgSelected: '#3b82f6', // azul 500 aprox.
      bgHover: '#2563eb', // azul 600 aprox.
      border: '#60a5fa',
      iconBase: '#60a5fa',
      textBase: TXT_BASE,
      labelColor: (selected: boolean, disabled: boolean) =>
        disabled ? DIS_TXT : selected ? '#fff' : TXT_BASE,
      iconColor: (selected: boolean, disabled: boolean) =>
        disabled ? DIS_TXT : selected ? '#fff' : '#60a5fa',
      disabledBg: DIS_BG,
      disabledTxt: DIS_TXT,
    };
  }

  // fallback
  const theme = getTheme();
  return {
    bgSelected: theme.palette.neutralLighter,
    bgHover: theme.palette.neutralLight,
    border: '#e1e1e1',
    iconBase: theme.palette.neutralSecondary,
    textBase: theme.palette.neutralPrimary,
    labelColor: (selected: boolean, disabled: boolean) =>
      disabled ? DIS_TXT : selected ? theme.palette.neutralPrimary : theme.palette.neutralPrimary,
    iconColor: (selected: boolean, disabled: boolean) =>
      disabled ? DIS_TXT : selected ? theme.palette.neutralPrimary : theme.palette.neutralSecondary,
    disabledBg: DIS_BG,
    disabledTxt: DIS_TXT,
  };
}

export function RamaButtons({
  value,
  options,
  onSelect,
  isDisabledForKey,
  label = 'Rama',
}: {
  value?: Key;
  options: IDropdownOption[];
  onSelect: (key: string) => void;
  isDisabledForKey: (key: string) => boolean;
  label?: string;
}) {
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const enabled = options.filter((o) => !isDisabledForKey(String(o.key)));
    if (!enabled.length) return;
    const idx = enabled.findIndex((o) => String(o.key) === String(value));
    const next =
      e.key === 'ArrowRight' || e.key === 'ArrowDown'
        ? enabled[(idx + 1 + enabled.length) % enabled.length]
        : e.key === 'ArrowLeft' || e.key === 'ArrowUp'
        ? enabled[(idx - 1 + enabled.length) % enabled.length]
        : null;

    if (next) {
      onSelect(String(next.key));
      e.preventDefault();
    }
  };

  return (
    <div role="radiogroup" aria-label={label} className={classes.group} onKeyDown={onKeyDown}>
      {options.map((opt, i) => {
        const k = String(opt.key);
        const selected = String(value) === k;
        const disabled = isDisabledForKey(k);
        const visual = getGenderVisual(k, opt.text);

        return (
          <React.Fragment key={k}>
            <DefaultButton
              className={classes.btn}
              text={opt.text ?? k}
              onClick={() => onSelect(k)}
              disabled={disabled}
              role="radio"
              aria-checked={selected}
              onRenderIcon={() => (
                <GenderIcon
                  tipo={/f/i.test(String(opt.text ?? k)) ? 'F' : 'M'}
                  size={16}
                  color={visual.iconColor(selected, disabled)}
                />
              )}
              styles={{
                root: {
                  border: 'none',
                  background: disabled ? visual.disabledBg : selected ? visual.bgSelected : undefined,
                  selectors: {
                    '.ms-Button-flexContainer': { columnGap: '4px', padding: '5px' },
                  },
                },
                icon: {
                  marginRight: 0, // “cinturón y tiradores” por si el theme no aplica columnGap
                },
                rootHovered: {
                  background: disabled ? visual.disabledBg : selected ? visual.bgHover : undefined,
                },
                rootPressed: {
                  background: disabled ? visual.disabledBg : selected ? visual.bgHover : undefined,
                },
                rootDisabled: {
                  background: visual.disabledBg,
                  color: visual.disabledTxt,
                },
                label: {
                  color: visual.labelColor(selected, disabled),
                  fontWeight: selected ? 700 : 600, // refuerza legibilidad al seleccionar
                },
              }}
            />
            {i < options.length - 1 && <div className={classes.divider} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}
